import { webStore, type KeyValueStore } from './session';

/**
 * Browser sign-in through nyce-logic-auth's `/authorize` (federated sign-in, e.g. Entra ID over SAML): authorization
 * code + PKCE (S256). `beginRedirectLogin` sends the browser to AUTH; AUTH returns to `redirectUri` with `code` and
 * `state`; `completeRedirectLogin` checks the state and hands back the code and its verifier. The code is redeemed
 * server-side (AUTH has no CORS): the app's backend posts `grant_type=authorization_code` with `code`, `client_id`,
 * `redirect_uri` and `code_verifier` to AUTH's `/api/auth/v1/token`.
 */

export interface RedirectLoginOptions {
  /** AUTH origin, e.g. `https://auth.nycelogic.com`. */
  authUrl: string;
  /** The app's login app id in AUTH (`client_id`). */
  clientId: string;
  /** Registered exactly in AUTH for the login app. */
  redirectUri: string;
  /** AUTH tenant id. */
  tenant: string;
  /** e.g. `offline_access`. */
  scope?: string;
  /** Anything the app wants back after the round trip (the page to return to, which environment …); JSON. */
  context?: unknown;
  /** Where the pending sign-in waits; default sessionStorage. */
  store?: KeyValueStore;
  /** Default `window.location.assign`. */
  navigate?: (url: string) => void;
}

export interface RedirectLoginResult {
  code: string;
  codeVerifier: string;
  redirectUri: string;
  clientId: string;
  context: unknown;
}

/** The sign-in came back with an OAuth error, or with a state this browser did not start. */
export class RedirectLoginError extends Error {
  constructor(
    readonly code: string,
    message: string,
    readonly context: unknown = undefined
  ) {
    super(message);
    this.name = 'RedirectLoginError';
  }
}

const PREFIX = 'nyk:login:';
const MAX_AGE_MS = 15 * 60_000;

interface Pending {
  verifier: string;
  redirectUri: string;
  clientId: string;
  context: unknown;
  at: number;
}

const defaultStore = () => webStore(typeof sessionStorage !== 'undefined' ? sessionStorage : undefined);

const base64Url = (bytes: Uint8Array) =>
  btoa(String.fromCharCode(...bytes))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');

const random = (n: number) => base64Url(crypto.getRandomValues(new Uint8Array(n)));

/** RFC 7636 S256: base64url(SHA-256(verifier)). */
export async function pkceChallenge(verifier: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));
  return base64Url(new Uint8Array(digest));
}

/** The `/authorize` URL (exported for tests and for apps that open it themselves). */
export function authorizeUrl(
  o: Pick<RedirectLoginOptions, 'authUrl' | 'clientId' | 'redirectUri' | 'tenant' | 'scope'>,
  state: string,
  challenge: string
): string {
  const q = new URLSearchParams({
    response_type: 'code',
    client_id: o.clientId,
    redirect_uri: o.redirectUri,
    tenant: o.tenant,
    state,
    code_challenge: challenge,
    code_challenge_method: 'S256',
  });
  if (o.scope) q.set('scope', o.scope);
  return `${o.authUrl.replace(/\/+$/, '')}/api/auth/v1/authorize?${q}`;
}

/** Remembers a new verifier and state, then navigates to AUTH. */
export async function beginRedirectLogin(o: RedirectLoginOptions): Promise<void> {
  const store = o.store ?? defaultStore();
  const verifier = random(32); // 43 characters
  const state = random(16);
  const pending: Pending = {
    verifier,
    redirectUri: o.redirectUri,
    clientId: o.clientId,
    context: o.context ?? null,
    at: Date.now(),
  };
  store.set(PREFIX + state, JSON.stringify(pending));
  const url = authorizeUrl(o, state, await pkceChallenge(verifier));
  (o.navigate ?? ((u) => window.location.assign(u)))(url);
}

/** True when the query is a sign-in coming back (`code` or `error` together with `state`). */
export function isRedirectLoginResponse(search: string | URLSearchParams): boolean {
  const q = typeof search === 'string' ? new URLSearchParams(search) : search;
  return q.has('state') && (q.has('code') || q.has('error'));
}

/**
 * Reads the return of `beginRedirectLogin` (the callback page's query). Each state is usable once. Throws
 * `RedirectLoginError` with AUTH's `error` (e.g. `access_denied`) or `invalid_state`.
 */
export function completeRedirectLogin(
  search: string | URLSearchParams,
  store: KeyValueStore = defaultStore()
): RedirectLoginResult {
  const q = typeof search === 'string' ? new URLSearchParams(search) : search;
  const state = q.get('state') ?? '';
  const raw = state ? store.get(PREFIX + state) : null;
  if (state) store.remove(PREFIX + state);
  let pending: Pending | null;
  try {
    pending = raw ? (JSON.parse(raw) as Pending) : null;
  } catch {
    pending = null;
  }
  if (!pending || Date.now() - pending.at > MAX_AGE_MS)
    throw new RedirectLoginError(
      'invalid_state',
      'This sign-in was not started here or has expired. Please try again.'
    );

  const error = q.get('error');
  if (error) throw new RedirectLoginError(error, q.get('error_description') || error, pending.context);
  const code = q.get('code');
  if (!code)
    throw new RedirectLoginError('invalid_request', 'The sign-in returned no code.', pending.context);
  return {
    code,
    codeVerifier: pending.verifier,
    redirectUri: pending.redirectUri,
    clientId: pending.clientId,
    context: pending.context,
  };
}
